import { DatePipe } from '@angular/common';
import { Directive } from '@angular/core';
import { AttributesValueType, CompanyAttributeDTO } from '@iota/iota-management-portal-v4-client';
import moment from 'moment';

export const ATTRIBUTE_ARRAY_VALUE_SEPARATOR = '#';

export enum AttributeNumberType {
    FLOAT,
    INTEGER
}

@Directive({
    standalone: false,
    selector: '[appAttributeValue]'
})
export class AttributeValueDirective {
    constructor(private datePipe: DatePipe) {}

    protected parseToStringArray(stringValue: string): Array<string> {
        return stringValue.split(ATTRIBUTE_ARRAY_VALUE_SEPARATOR).filter((value) => value.trim() !== '');
    }

    private parseToDateArray(stringValue: string, format: string): Array<Date> {
        return stringValue
            .split(ATTRIBUTE_ARRAY_VALUE_SEPARATOR)
            .filter((value) => value.trim() !== '')
            .map((dateString) => {
                if (moment(dateString, format).isValid()) {
                    return moment.utc(dateString, format) as any;
                }

                return undefined;
            });
    }

    private parseToBooleanArray(stringValue: string): Array<boolean> {
        return stringValue
            .split(ATTRIBUTE_ARRAY_VALUE_SEPARATOR)
            .filter((value) => value.trim() !== '')
            .map((value) => {
                if (value.trim() === 'true') {
                    return true;
                }

                if (value.trim() === 'false') {
                    return false;
                }

                return undefined;
            });
    }

    protected parseToNumberArray(stringValue: string, type: AttributeNumberType): Array<number> {
        return stringValue
            .split(ATTRIBUTE_ARRAY_VALUE_SEPARATOR)
            .filter((value) => value.trim() !== '')
            .map((value) => (type === AttributeNumberType.INTEGER ? this.parseToInt(value) : this.parseToFloat(value)));
    }

    protected parseToInt(stringValue: string): number {
        if (!isNaN(parseInt(stringValue))) {
            return parseInt(stringValue);
        }
        return undefined;
    }

    protected parseToFloat(stringValue: string): number {
        if (!isNaN(parseFloat(stringValue))) {
            return parseFloat(stringValue);
        }
        return undefined;
    }

    protected parseAttributeValue(valueAttribute: CompanyAttributeDTO, valueType: AttributesValueType): string {
        if (!valueAttribute) return '';

        switch (valueType) {
            case AttributesValueType.BOOL:
                return this.joinValues(valueAttribute.booleanValue);
            case AttributesValueType.DATE:
                return this.joinDateValues(valueAttribute.dateValue);
            case AttributesValueType.DATETIME:
                return this.joinDateTimeValues(valueAttribute.dateTimeValue);
            case AttributesValueType.NUMBER:
                return this.joinValues(valueAttribute.numValue);
            case AttributesValueType.INTEGER:
                return this.joinValues(valueAttribute.intValue);
            case AttributesValueType.STRING:
                return this.joinValues(valueAttribute.strValue);
            case AttributesValueType.UUID:
                return this.joinValues(valueAttribute.uuidValue);
            default:
                return '';
        }
    }

    protected getAttributeValueArray(internalValue: string, valueType: AttributesValueType): CompanyAttributeDTO {
        const attributeValues: CompanyAttributeDTO = {};

        switch (valueType) {
            case AttributesValueType.BOOL:
                attributeValues.booleanValue = this.parseToBooleanArray(internalValue);
                break;
            case AttributesValueType.DATE:
                attributeValues.dateValue = this.parseToDateArray(internalValue, 'DD.MM.YYYY');
                break;
            case AttributesValueType.DATETIME:
                attributeValues.dateTimeValue = this.parseToDateArray(internalValue, 'DD.MM.YYYY hh:mm:ss');
                break;
            case AttributesValueType.NUMBER:
                attributeValues.numValue = this.parseToNumberArray(internalValue, AttributeNumberType.FLOAT);
                break;
            case AttributesValueType.INTEGER:
                attributeValues.intValue = this.parseToNumberArray(internalValue, AttributeNumberType.INTEGER);
                break;
            case AttributesValueType.STRING:
                attributeValues.strValue = this.parseToStringArray(internalValue);
                break;
            case AttributesValueType.UUID:
                attributeValues.uuidValue = this.parseToStringArray(internalValue);
                break;
        }

        return attributeValues;
    }

    protected joinValues(values: any[]): string {
        return Array.isArray(values) ? values.join(ATTRIBUTE_ARRAY_VALUE_SEPARATOR) : '';
    }

    private joinDateValues(values: Date[]): string {
        return Array.isArray(values)
            ? values.map((v) => this.datePipe.transform(v, 'dd.MM.yyyy')).join(ATTRIBUTE_ARRAY_VALUE_SEPARATOR)
            : '';
    }

    private joinDateTimeValues(values: Date[]): string {
        return Array.isArray(values)
            ? values.map((v) => this.datePipe.transform(v, 'dd.MM.yyyy HH:mm:ss')).join(ATTRIBUTE_ARRAY_VALUE_SEPARATOR)
            : '';
    }
}
